import toNumber from 'lodash/toNumber';
import {
	type TerminalClause,
	OPERATOR_GT_EQUALS,
	OPERATOR_LT_EQUALS,
	OPERAND_TYPE_VALUE,
} from '@atlaskit/jql-ast';
import type { MessageDescriptor } from '@atlassian/jira-intl';
import type { JqlData } from '@atlassian/jira-jql-utils/src/utils/types.tsx';
import {
	isUnsubmittedValueNotANumber,
	isUnsubmittedNumberTooLarge,
} from '../../../../common/ui/utils.tsx';
import { removeSurroundingQuotes } from '../../../../utils/remove-surrounding-quotes.tsx';
import messages from './messages.tsx';
import type {
	NumberRangeValue,
	NumberRangeValueErrorMessageDescriptors,
	NumberRangeValueErrorMessageDescriptorsKeys,
	NumberRangeValueKeys,
} from './types.tsx';

export const isNumberRangeValueEmpty = (numberRangeValue: NumberRangeValue): boolean =>
	numberRangeValue.min === '' && numberRangeValue.max === '';

export const hasNumberRangeValueError = (
	errorStateMessages: NumberRangeValueErrorMessageDescriptors,
): boolean => errorStateMessages.min !== undefined || errorStateMessages.max !== undefined;

/**
 * Transform incoming Terminal Clauses into JqlData, performing data validation.
 * An empty JqlData object is returned if no clauses are provided.
 * This does not handle restrictions on allowed operators.
 */
export const getJqlDataFromClauses = (
	primaryClause?: TerminalClause,
	secondaryClause?: TerminalClause,
): JqlData => ({
	...(primaryClause !== undefined &&
		primaryClause.operator &&
		primaryClause.operand &&
		primaryClause.operand.operandType === OPERAND_TYPE_VALUE && {
			primaryClause: {
				operator: primaryClause.operator.value,
				operand: removeSurroundingQuotes(primaryClause.operand.text),
			},
		}),
	...(secondaryClause !== undefined &&
		secondaryClause.operator &&
		secondaryClause.operand &&
		secondaryClause.operand.operandType === OPERAND_TYPE_VALUE && {
			secondaryClause: {
				operator: secondaryClause.operator.value,
				operand: removeSurroundingQuotes(secondaryClause.operand.text),
			},
		}),
});

/**
 * Transform JqlData to NumberRangeValue which can be used in NumberRangePicker
 */
export const mapJqlDataToNumberRangeValue = (jqlData: JqlData): NumberRangeValue => {
	const { primaryClause, secondaryClause } = jqlData;
	const numberRangeValue = { min: '', max: '' };
	[primaryClause, secondaryClause].forEach((clause) => {
		switch (clause?.operator) {
			case OPERATOR_GT_EQUALS: {
				numberRangeValue.min = clause.operand;
				break;
			}
			case OPERATOR_LT_EQUALS: {
				numberRangeValue.max = clause.operand;
				break;
			}
			default:
				break;
		}
	});
	return numberRangeValue;
};

/**
 * Transform NumberRangeValue to JqlData which can be used to update the AST
 */
export const mapNumberRangeValueToJqlData = (numberRangeValue: NumberRangeValue): JqlData => ({
	...(numberRangeValue.min !== '' && {
		primaryClause: {
			operator: OPERATOR_GT_EQUALS,
			operand: numberRangeValue.min,
		},
	}),
	...(numberRangeValue.max !== '' && {
		secondaryClause: {
			operator: OPERATOR_LT_EQUALS,
			operand: numberRangeValue.max,
		},
	}),
});

/**
 * Calculate the trigger value's message descriptor based on the selected NumberRangeValue
 */
export const getTriggerValueMessageDescriptor = (
	numberRangeValue: NumberRangeValue,
): MessageDescriptor | undefined => {
	const { min, max } = numberRangeValue;
	if (min !== '' && max !== '') {
		return messages.triggerValueBothClauses;
	}
	if (min !== '') {
		return messages.triggerValueMinimumClause;
	}
	if (max !== '') {
		return messages.triggerValueMaximumClause;
	}
};

export const isUnsubmittedValueNumberRangeInvalid = (
	inputValue: NumberRangeValue,
	focusedIndex: NumberRangeValueKeys,
): boolean => {
	if (inputValue.min !== '' && inputValue.max !== '') {
		if (
			focusedIndex === 'min' &&
			toNumber(inputValue.min) >
				(inputValue.max !== '' ? toNumber(inputValue.max) : Number.POSITIVE_INFINITY)
		) {
			return true;
		}

		if (
			focusedIndex === 'max' &&
			toNumber(inputValue.max) <
				(inputValue.min !== '' ? toNumber(inputValue.min) : Number.NEGATIVE_INFINITY)
		) {
			return true;
		}
	}
	return false;
};

export const getNumberRangePickerErrorMessages = (
	inputValue: NumberRangeValue,
	// default is used here to account for initial state, where focusedIndex is not provided
	focusedIndex: NumberRangeValueKeys = 'max',
): NumberRangeValueErrorMessageDescriptors => {
	const errorMessages: NumberRangeValueErrorMessageDescriptors = {};
	if (isNumberRangeValueEmpty(inputValue)) {
		return errorMessages;
	}

	// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
	for (const index of ['min', 'max'] as NumberRangeValueKeys[]) {
		if (focusedIndex === index && isUnsubmittedValueNumberRangeInvalid(inputValue, index)) {
			// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
			errorMessages[index as NumberRangeValueErrorMessageDescriptorsKeys] =
				focusedIndex === 'min'
					? messages.numberRangeErrorMinimumClauseMessage
					: messages.numberRangeErrorMaximumClauseMessage;
		}
		if (isUnsubmittedValueNotANumber(inputValue[index])) {
			// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
			errorMessages[index as NumberRangeValueErrorMessageDescriptorsKeys] =
				messages.validNumberErrorMessage;
		}
		// this assumes that value is a valid number
		if (isUnsubmittedNumberTooLarge(toNumber(inputValue[index]))) {
			// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
			errorMessages[index as NumberRangeValueErrorMessageDescriptorsKeys] =
				messages.numberSizeLimitErrorMessage;
		}
	}

	return errorMessages;
};
